import { useRollbar } from '@rollbar/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { RecordFactory } from '../../models/RecordFactory'
import { defaultPathForLoginResponse } from '../../modules/apiclient'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import Badge from '../app/design/Badge'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Tag from '../app/design/Tag'
import { SelectList, SelectListItem } from '../common/SelectList'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { LoginAndSecurityModal } from './LoginAndSecurityModal'

type Props = {
    isOpen: boolean
    onClose: () => void
}

export const AccountSwitcherModal = ({ isOpen, onClose }: Props) => {
    const me = PSApplication.me()

    const rollbar = useRollbar()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const { logAndDisplayError } = useErrorHandler()

    const [isLoginAndSecurityModalOpen, setIsLoginAndSecurityModalOpen] = useState(false)
    const [isSwitchingAccount, setIsSwitchingAccount] = useState(false)

    const { data: associatedAccountUsers } = useQuery({
        queryKey: ['account-switcher-users'],
        queryFn: async () => {
            const result = await RecordFactory.getInstance().fetchRoles('switch')
            if (!result.ok) {
                throw result.error
            }
            return result.value
        },
        enabled: isOpen,
    })

    const listItems = (() => {
        if (associatedAccountUsers === undefined) {
            return []
        }

        return associatedAccountUsers
            .filter((user) => !((user.solo || user.company) && user.requiresUpgrade))
            .map((user): SelectListItem & { user: typeof user } => ({
                id: user.id ?? '',
                title: (
                    <span className="flex items-center justify-start gap-x-2">
                        {(user.entity || user.name) ?? ''}{' '}
                        {!user.lastSyncDate && <Tag label="New" size="sm" intent="danger" />}
                    </span>
                ),
                subtitle: user.canLoginViaAccountSwitcher
                    ? user.roleSubtitle
                    : `${user.roleSubtitle} · Not supported on desktop`,
                tertiaryView: (
                    <div className="relative">
                        <ContactAvatar user={user} />
                        {user.roleBadge !== undefined && user.roleBadge > 0 && (
                            <Badge value={user.roleBadge} className="absolute -top-1 -right-1" />
                        )}
                    </div>
                ),
                isDisabled: !user.canLoginViaAccountSwitcher,
                user,
            }))
    })()
    const selectedItem = listItems.find((item) => item.id === me.id)

    const handleSelectUserItems = async (items: typeof listItems) => {
        const user = items[0]?.user
        if (user === undefined || user.id === undefined) {
            return
        }

        try {
            setIsSwitchingAccount(true)
            const loginResponse = await RecordFactory.getInstance().login(
                { associatedAccountUserID: user.id },
                queryClient,
                rollbar
            )
            if (!loginResponse.ok) {
                setIsSwitchingAccount(false)
                return
            }

            const newPath = defaultPathForLoginResponse(loginResponse.value)
            if (newPath !== window.location.pathname) {
                window.location.href = newPath
            }
            else {
                window.location.reload()
            }
        }
        catch (error) {
            setIsSwitchingAccount(false)
            logAndDisplayError(error)
        }
    }

    return (
        <>
            <Modal
                isOpen={isOpen && !isLoginAndSecurityModalOpen}
                onClose={onClose}
                title="Accounts"
                footer={
                    <div className="flex w-full items-center justify-between">
                        <Button
                            intent="secondary"
                            onClick={() => setIsLoginAndSecurityModalOpen(true)}
                        >
                            Login, security & user settings
                        </Button>
                        <Button
                            intent="secondary"
                            onClick={() => {
                                onClose()
                                navigate('/signup')
                            }}
                        >
                            Add account
                        </Button>
                    </div>
                }
            >
                {isSwitchingAccount ? (
                    <div className="flex w-full flex-col items-center justify-center gap-y-4">
                        <Spinner size={64} />
                        <Label>Switching accounts...</Label>
                    </div>
                ) : associatedAccountUsers !== undefined ? (
                    <SelectList
                        selectionType="single"
                        items={listItems}
                        selectedItems={selectedItem ? [selectedItem] : []}
                        onSelectItems={handleSelectUserItems}
                    />
                ) : (
                    <div className="flex items-center justify-center">
                        <Spinner size={48} />
                    </div>
                )}
            </Modal>

            {isLoginAndSecurityModalOpen && (
                <LoginAndSecurityModal
                    isOpen={isLoginAndSecurityModalOpen}
                    onClose={() => {
                        setIsLoginAndSecurityModalOpen(false)
                        onClose()
                    }}
                    onBack={() => setIsLoginAndSecurityModalOpen(false)}
                />
            )}
        </>
    )
}
